import { type PropsWithChildren, type ReactNode } from 'react';

import { type RequestContext } from '~/vendor/cx-web/domain-types';

import { type BetInsightsConfig } from '../BetInsightsConfiguration';
import { type BettingInsightsContent, type BettingInsightsResult, type EsIndex } from '../types';

import { type BettingInsightsTableConfig, createBettingInsightsTable } from './BettingInsightsTable';
import { type BettingInsightsTitleConfig, createBettingInsightsTitle } from './BettingInsightsTitle';

export type WrapperParams<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>> = {
  config: BetInsightsConfig<TIndex, TContent>;
  content: BettingInsightsContent<TIndex>[];
  resourcesUrl?: string;
  result: BettingInsightsResult<TIndex>;
  resultIndex?: number;
} & PropsWithChildren;

// Type for the context that will be passed as first parameter to wrapper function
export interface BettingInsightsCardContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsCardConfig<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>> {
  styles: { readonly [key: string]: string };
  table: BettingInsightsTableConfig<TIndex, TContent>;
  title: BettingInsightsTitleConfig<TIndex>;
  wrapper: (
    context: BettingInsightsCardContext,
    { children, config, content, resourcesUrl, result, resultIndex }: WrapperParams<TIndex, TContent>,
  ) => ReactNode;
}

// Type alias for the return type of createBettingInsightsCard
export type BettingInsightsCard<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>> = Readonly<{
  styles: { readonly [key: string]: string };
  table: ReturnType<typeof createBettingInsightsTable<TIndex, TContent>>;
  title: ReturnType<typeof createBettingInsightsTitle<TIndex>>;
  wrapper: ({
    children,
    config,
    content,
    resourcesUrl,
    result,
    resultIndex,
  }: WrapperParams<TIndex, TContent>) => ReactNode;
}>;

/**
 * Factory function to create BettingInsightsCard configuration (functional approach)
 * @param requestContext - The request context containing environment, feature flags, repub config, and user details
 * @param config - The card configuration with table, title, wrapper, and styles
 * @returns Immutable card configuration object with bound wrapper method
 * @example
 * ```typescript
 * const card = createBettingInsightsCard(requestContext, {
 *   styles: cardStyles,
 *   table: tableConfig,
 *   title: titleConfig,
 *   wrapper(context, { children, config, content, result }) {
 *     // context has access to environment, featureFlags, repubConfig, user
 *     return <div className="card">{children}</div>;
 *   }
 * });
 * ```
 */
export function createBettingInsightsCard<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>>(
  requestContext: RequestContext,
  config: BettingInsightsCardConfig<TIndex, TContent>,
): BettingInsightsCard<TIndex, TContent> {
  // Create context object
  const context: BettingInsightsCardContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
  };

  // Create the result object with wrapper function that injects context
  const result = {
    // Config properties
    styles: config.styles,
    table: createBettingInsightsTable(requestContext, config.table),
    title: createBettingInsightsTitle(requestContext, config.title),

    // Wrapper function that injects context as first parameter
    wrapper: ({
      children,
      config: wrapperConfig,
      content,
      resourcesUrl,
      result: wrapperResult,
      resultIndex,
    }: WrapperParams<TIndex, TContent>) =>
      config.wrapper(context, {
        children,
        config: wrapperConfig,
        content,
        resourcesUrl,
        result: wrapperResult,
        resultIndex,
      }),
  };

  return Object.freeze(result);
}
